/** @jsxImportSource @emotion/react */
// React/core
import React, { useState, useMemo, useCallback, useEffect } from 'react'
// Third-party
import { css } from '@emotion/react'
import { Switch, Spin, App, Tooltip, Table, Dropdown } from 'antd'
import type { MenuProps } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  UniqueIdentifier,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
// Relative
import Icon from 'src/components/Icon'
import { FilterDropdown } from './ScheduleFilters'
import type { FilterOption } from './ScheduleFilters'
import AddConstraintModal from './AddConstraintModal'
import AiSuggestModal from './AiSuggestModal'
import MatchupMatrixModal from './MatchupMatrixModal'
import type { MatchupMatrixModalDivision } from './MatchupMatrixModal'
import type { TimeSlot } from '../hooks/useSchedulerTimeSlots'
import {
  buildCollapsedPairMatchupRow,
  buildCollapsedDivisionTargetRow,
  divisionUidsWithTargets,
  isPairRowSupersededByTarget,
  COLLAPSED_PAIR_MATCHUP_ID,
  COLLAPSED_DIVISION_TARGET_ID,
  PAIR_MATCHUP_COUNT_CODE,
  type CollapsedPairMatchupRow,
  type CollapsedDivisionTargetRow,
} from './matchupCollapseHelpers'
import { gamesPerTeamPromiseCopy } from './matchupMatrixHelpers'
import type { SavedDivisionTargetRow } from './matchupMatrixHelpers'
import {
  BLACKOUT_BYE_CONSTRAINT_CODES,
  mapCategoryToDisplay,
  getConstraintDisplayName,
  formatConstraintParameterSummary,
} from '../constants'
import {
  constraintDescriptionStyles,
  constraintTitleStyles,
  darkTableStyles,
  outlineAccentButtonStyles,
  outlinePrimaryButtonStyles,
} from '../styles/shared'
import type {
  SchedulerConstraint,
  ReorderItem,
  CreateConstraintData,
  BulkConstraintOps,
  BulkConstraintsResult,
} from '../hooks/useSchedulerConstraints'
import type { Venue } from '../hooks/useSchedulerLocations'
import useConstraintTypes from '../hooks/useConstraintTypes'
import ConstraintsTutorial from './ConstraintsTutorial'

/**
 * Division type from parent component
 */
interface Division {
  uid: string
  name: string
}

/**
 * Team type from parent component
 */
interface Team {
  uid: string
  name: string
  divisionUid: string
}

/**
 * A placeholder row for constraint types not yet configured
 */
interface PlaceholderRow {
  _isPlaceholder: true
  _isCollapsed?: false
  _isCollapsedTarget?: false
  id: string
  type: {
    id: string
    code: string
    name: string
    category: string
  }
  is_hard: boolean
  is_enabled: false
  description: string | null
  division_uids: null
  team_uids: null
  sort_order: number
  parameters: Record<string, unknown>
}

type TableRow =
  | (SchedulerConstraint & {
      _isPlaceholder?: false
      _isCollapsed?: false
      _isCollapsedTarget?: false
    })
  | PlaceholderRow
  | (CollapsedPairMatchupRow & {
      _isPlaceholder?: false
      _isCollapsedTarget?: false
    })
  | (CollapsedDivisionTargetRow & {
      _isPlaceholder?: false
      _isCollapsed?: false
    })

/**
 * Props for ConstraintsSection
 */
interface ConstraintsSectionProps {
  flightId: string | null
  divisions?: Division[]
  teams?: Team[]
  venues?: Venue[]
  constraints: SchedulerConstraint[]
  loading: boolean
  toggleEnabled: (constraintId: string, enabled: boolean) => Promise<void>
  updateConstraint: (
    constraintId: string,
    data: Partial<{
      parameters: Record<string, unknown>
      description: string
      is_hard: boolean
      is_enabled: boolean
      priority: number
      weight: number
      division_uids: string[] | null
      team_uids: string[] | null
      sort_order: number
    }>
  ) => Promise<SchedulerConstraint>
  reorderConstraints: (orderedItems: ReorderItem[]) => Promise<void>
  deleteConstraint: (constraintId: string) => Promise<void>
  addConstraint: (data: CreateConstraintData) => Promise<SchedulerConstraint>
  /**
   * Bulk add/update/delete constraints in one atomic request. Forwarded
   * to MatchupMatrixModal so saving the matrix becomes one POST instead
   * of N concurrent ones.
   */
  bulkSaveConstraints: (
    ops: BulkConstraintOps
  ) => Promise<BulkConstraintsResult>
  /** Refetch the constraints list — used by the AI Suggest accept flow to refresh the table after server-side creates. */
  reloadConstraints?: () => Promise<unknown>
  /**
   * Saved per-division targets from GET /division-targets. The Games Per Team
   * modal persists these (and deletes the legacy pair_matchup_count rows), so
   * the list builds its "Games Per Team" summary row from here.
   */
  divisionTargets?: SavedDivisionTargetRow[]
  /** Refetch the division targets — called after the Games Per Team modal saves. */
  reloadDivisionTargets?: () => Promise<unknown>
  /**
   * Reset the given divisions to the fill-feasible-slots default. Backs the
   * toggle-off / Delete affordance on the Games Per Team summary row (revert
   * a target to "fill" without opening the modal).
   */
  clearDivisionTargets?: (divisionUids: string[]) => Promise<void>
  /**
   * Uniform default games-per-matchup, used by the matrix modal when an
   * admin opens it via the pair_matchup_count row/placeholder. Cells equal
   * to this value aren't stored as overrides.
   */
  defaultGamesPerMatchup?: number
  /**
   * Active time slot templates for the current flight. Forwarded to the
   * matchup modal so it can estimate per-division feasibility (capacity vs
   * needed games) and surface inline ✓ / ⚠ / ✗ chips before the user saves.
   */
  timeSlots?: TimeSlot[]
  /** Flight date range — drives the slot-expansion estimate. */
  flightStartDate?: string | null
  flightEndDate?: string | null
}

/**
 * Format division UIDs to display string
 */
const formatDivisions = (
  divisionUids: string[] | null,
  divisions: Division[]
): string => {
  if (!divisionUids || divisionUids.length === 0) {
    return 'All Divisions'
  }

  // If every available division is selected, show "All Divisions"
  if (
    divisions.length > 0 &&
    divisionUids.length >= divisions.length &&
    divisions.every((d) => divisionUids.includes(d.uid))
  ) {
    return 'All Divisions'
  }

  const divisionNames = divisionUids
    .map((uid) => {
      const division = divisions.find((d) => d.uid === uid)
      return division?.name || uid
    })
    .filter(Boolean)

  if (divisionNames.length === 0) {
    return 'All Divisions'
  }

  if (divisionNames.length <= 2) {
    return divisionNames.join(', ')
  }

  return `${divisionNames[0]}, ${divisionNames[1]} +${divisionNames.length - 2}`
}

/**
 * Format team UIDs to display string
 */
const formatTeams = (teamUids: string[] | null, teams: Team[]): string => {
  if (!teamUids || teamUids.length === 0) {
    return 'All Teams'
  }

  // If every available team is selected, show "All Teams"
  if (
    teams.length > 0 &&
    teamUids.length >= teams.length &&
    teams.every((t) => teamUids.includes(t.uid))
  ) {
    return 'All Teams'
  }

  const teamNames = teamUids
    .map((uid) => {
      const team = teams.find((t) => t.uid === uid)
      return team?.name || uid
    })
    .filter(Boolean)

  if (teamNames.length === 0) {
    return 'All Teams'
  }

  if (teamNames.length <= 2) {
    return teamNames.join(', ')
  }

  return `${teamNames[0]}, ${teamNames[1]} +${teamNames.length - 2}`
}

/**
 * Sortable row wrapper — only applies dnd-kit sorting to real rows
 */
const SortableRow: React.FC<{
  id: string
  children?: React.ReactNode
  style?: React.CSSProperties
  className?: string
  [key: string]: unknown
}> = ({ id, children, style, className, ...rest }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const rowStyle: React.CSSProperties = {
    ...style,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <tr
      ref={setNodeRef}
      style={rowStyle}
      className={className}
      {...attributes}
      {...listeners}
      {...rest}
    >
      {children}
    </tr>
  )
}

/**
 * Static row for placeholder entries — no dnd-kit interaction
 */
const PlaceholderTableRow: React.FC<{
  children?: React.ReactNode
  style?: React.CSSProperties
  className?: string
  [key: string]: unknown
}> = ({ children, style, className, ...rest }) => (
  <tr style={style} className={`${className || ''} placeholder-row`} {...rest}>
    {children}
  </tr>
)

/**
 * Dispatches to SortableRow or PlaceholderTableRow based on row key
 */
const DraggableRow: React.FC<{
  id?: string
  children?: React.ReactNode
  style?: React.CSSProperties
  className?: string
  [key: string]: unknown
}> = (props) => {
  const rowKey = (props as Record<string, unknown>)['data-row-key'] as
    | string
    | undefined
  if (rowKey?.startsWith('placeholder-')) {
    return <PlaceholderTableRow {...props} />
  }
  // The collapsed division-target row isn't draggable (its id is absent from
  // SortableContext) and isn't a placeholder — render a plain static <tr> so
  // it neither calls useSortable nor inherits the dimmed placeholder styling.
  if (rowKey === COLLAPSED_DIVISION_TARGET_ID) {
    return <tr {...props} />
  }
  if (rowKey) {
    // Includes COLLAPSED_PAIR_MATCHUP_ID — it's now a sortable row that
    // expands into all underlying pair_matchup_count constraint IDs in
    // handleDragEnd, so dragging it reorders the whole cluster as a unit.
    return <SortableRow id={rowKey} {...props} />
  }
  return <tr {...props} />
}

/**
 * Get category badge styles
 */
const getCategoryStyles = (category: string) => {
  switch (category) {
    case 'Time':
      return {
        borderColor: colors.WARNING_200,
        color: colors.WARNING_200,
        backgroundColor: 'rgba(58, 41, 5, 0.3)',
      }
    case 'Game':
      return {
        borderColor: colors.SUCCESS_200,
        color: colors.SUCCESS_200,
        backgroundColor: 'rgba(5, 58, 17, 0.3)',
      }
    case 'Availability':
      return {
        borderColor: colors.ORANGE,
        color: colors.ORANGE,
        backgroundColor: 'rgba(58, 30, 5, 0.3)',
      }
    case 'Dates':
      return {
        borderColor: colors.ERROR_100,
        color: colors.ERROR_100,
        backgroundColor: 'rgba(132, 16, 16, 0.3)',
      }
    case 'Distribution':
      return {
        borderColor: colors.SUCCESS_200,
        color: colors.SUCCESS_200,
        backgroundColor: 'rgba(5, 58, 17, 0.3)',
      }
    default:
      return {
        borderColor: colors.NEUTRAL_100,
        color: colors.NEUTRAL_100,
        backgroundColor: 'transparent',
      }
  }
}

/**
 * Render a category badge
 */
const renderCategoryBadge = (category: string) => {
  const styles = getCategoryStyles(category)
  return (
    <span
      css={css`
        font-family: 'Inter', sans-serif;
        font-size: 12px;
        font-weight: 400;
        line-height: 17.4px;
        padding: 2px 4px;
        background-color: ${styles.backgroundColor};
        border: 1px solid ${styles.borderColor};
        color: ${styles.color};
        border-radius: 4px;
        white-space: nowrap;
      `}
    >
      {category}
    </span>
  )
}

export const ConstraintsSection: React.FC<ConstraintsSectionProps> = ({
  flightId: _flightId,
  divisions = [],
  teams = [],
  venues = [],
  constraints,
  loading,
  toggleEnabled,
  updateConstraint,
  reorderConstraints,
  deleteConstraint,
  addConstraint,
  bulkSaveConstraints,
  reloadConstraints,
  divisionTargets = [],
  reloadDivisionTargets,
  clearDivisionTargets,
  defaultGamesPerMatchup = 1,
  timeSlots = [],
  flightStartDate = null,
  flightEndDate = null,
}) => {
  const { message, modal } = App.useApp()

  // Extract facilities from venues for the modal
  const venuesForModal = useMemo(
    () => venues.map((v) => ({ uid: v.id, name: v.name })),
    [venues]
  )

  const facilitiesForModal = useMemo(
    () =>
      venues.flatMap((v) =>
        v.facilities.map((f) => ({
          uid: f.id,
          name: `${v.name} - ${f.name}`,
        }))
      ),
    [venues]
  )

  // uid -> display name for entity-referencing constraint summaries
  // (facility_distribution percentage targets name venues and facilities
  // by uid).
  const constraintEntityNames = useMemo(() => {
    const names: Record<string, string> = {}
    venues.forEach((v) => {
      names[v.id] = v.name
      v.facilities.forEach((f) => {
        names[f.id] = `${v.name} - ${f.name}`
      })
    })
    return names
  }, [venues])

  const { constraintTypes } = useConstraintTypes()
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isAiSuggestModalOpen, setIsAiSuggestModalOpen] = useState(false)
  const [editingConstraint, setEditingConstraint] =
    useState<SchedulerConstraint | null>(null)
  const [preSelectedTypeCode, setPreSelectedTypeCode] = useState<string | null>(
    null
  )
  // Matchup-matrix modal state — pair_matchup_count constraints are edited
  // exclusively through the per-division grid modal. Division selection is
  // owned by the modal itself (in-place dropdown); we just pass the
  // preferred starting division when opening.
  const [matchupModalOpen, setMatchupModalOpen] = useState(false)
  const [matchupInitialDivisionId, setMatchupInitialDivisionId] = useState<
    string | null
  >(null)
  const [divisionFilter, setDivisionFilter] = useState<string[]>([])
  const [teamFilter, setTeamFilter] = useState<string[]>([])
  const [tutorialOpen, setTutorialOpen] = useState(false)

  // Build filter options
  const divisionFilterOptions: FilterOption[] = useMemo(
    () => divisions.map((d) => ({ label: d.name, value: d.uid })),
    [divisions]
  )

  const teamFilterOptions: FilterOption[] = useMemo(() => {
    const filtered =
      divisionFilter.length > 0
        ? teams.filter((t) => divisionFilter.includes(t.divisionUid))
        : teams
    return filtered.map((t) => ({ label: t.name, value: t.uid }))
  }, [teams, divisionFilter])

  const isFiltered = divisionFilter.length > 0 || teamFilter.length > 0

  const handleDivisionFilterChange = useCallback(
    (value: string[]) => {
      setDivisionFilter(value)
      // Prune team selections that are no longer valid
      if (value.length > 0) {
        const validTeamUids = new Set(
          teams.filter((t) => value.includes(t.divisionUid)).map((t) => t.uid)
        )
        setTeamFilter((prev) => prev.filter((uid) => validTeamUids.has(uid)))
      }
    },
    [teams]
  )

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: isFiltered ? Infinity : 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Build merged table data: real constraints + placeholder rows
  const tableData: TableRow[] = useMemo(() => {
    // Division team-count metadata, derived from the divisions + teams props
    // directly. (Do NOT reference matrixDivisions here — it's declared after
    // this memo, so it's in the temporal dead zone.)
    const divisionMeta = divisions.map((d) => ({
      id: d.uid,
      name: d.name,
      teamCount: teams.filter((t) => t.divisionUid === d.uid).length,
    }))

    // Divisions that carry an explicit /division-targets value. The target row
    // takes precedence over legacy pair_matchup_count display for these.
    const targetDivSet = divisionUidsWithTargets(divisionTargets)

    // team uid → division uid, for resolving global (unscoped) pair rows.
    const teamToDivision = new Map<string, string>()
    for (const t of teams) {
      teamToDivision.set(t.uid, t.divisionUid)
    }

    // 1. Real constraints (exclude blackout/bye + collapse pair_matchup_count
    //    into a single synthetic summary row — see matchupCollapseHelpers).
    const eligibleConstraints = constraints.filter(
      (c) => !BLACKOUT_BYE_CONSTRAINT_CODES.includes(c.type.code)
    )
    // Drop legacy pair rows whose division already has a target — the target
    // row supersedes their (now stale) display. The collapsed row's
    // _collapsedIds (and so the bulk delete/toggle/type actions on it) inherit
    // this exclusion, so those actions never touch a superseded pair row.
    const pairMatchupRows = eligibleConstraints.filter(
      (c) =>
        c.type.code === PAIR_MATCHUP_COUNT_CODE &&
        !isPairRowSupersededByTarget(c, targetDivSet, teamToDivision)
    )
    const otherRealRows: TableRow[] = eligibleConstraints
      .filter((c) => c.type.code !== PAIR_MATCHUP_COUNT_CODE)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((c) => ({ ...c, _isPlaceholder: false as const }))

    const collapsedPair = buildCollapsedPairMatchupRow(pairMatchupRows)
    const collapsedTarget = buildCollapsedDivisionTargetRow(
      divisionTargets,
      divisionMeta
    )
    let realRows: TableRow[] = otherRealRows
    if (collapsedPair) {
      realRows = [...realRows, collapsedPair]
    }
    if (collapsedTarget) {
      realRows = [...realRows, collapsedTarget]
    }
    realRows = realRows.sort((a, b) => a.sort_order - b.sort_order)

    // 2. Find which types already have at least one real constraint (check all, including blackout/bye)
    const configuredCodes = new Set(constraints.map((c) => c.type.code))
    // When the synthetic target row is present, treat pair_matchup_count as
    // configured so its unconfigured-type placeholder isn't emitted alongside
    // the real target row (avoids a duplicate disabled "Games per Team" row).
    if (collapsedTarget) {
      configuredCodes.add(PAIR_MATCHUP_COUNT_CODE)
    }

    // 3. Build placeholder rows for ALL unconfigured types
    const placeholders: TableRow[] = constraintTypes
      .filter((t) => !configuredCodes.has(t.code))
      .map((typeDef, idx) => ({
        _isPlaceholder: true as const,
        id: `placeholder-${typeDef.code}`,
        type: {
          id: typeDef.id,
          code: typeDef.code,
          name: getConstraintDisplayName(typeDef.code, typeDef.name),
          category: typeDef.category,
        },
        is_hard: typeDef.is_hard_default,
        is_enabled: false as const,
        description: typeDef.description,
        division_uids: null,
        team_uids: null,
        sort_order: 9000 + idx,
        parameters: {},
      }))

    // 4. Apply division/team filters (only to real rows; placeholders always visible)
    let filtered = realRows
    if (isFiltered) {
      filtered = realRows.filter((c) => {
        if (
          divisionFilter.length > 0 &&
          c.division_uids !== null &&
          !c.division_uids.some((uid) => divisionFilter.includes(uid))
        ) {
          return false
        }
        if (
          teamFilter.length > 0 &&
          c.team_uids !== null &&
          !c.team_uids.some((uid) => teamFilter.includes(uid))
        ) {
          return false
        }
        return true
      })
    }

    // Group configured rows: active first, disabled second (both already in
    // sort_order). The Priority column numbers active rows by table index and
    // renders disabled rows as "—", so an interleaved disabled row makes the
    // next active row skip a number. Grouping disabled rows below the active
    // ones keeps the active sequence gap-free. Placeholders stay last.
    const activeConfigured = filtered.filter((r) => r.is_enabled)
    const disabledConfigured = filtered.filter((r) => !r.is_enabled)
    return [...activeConfigured, ...disabledConfigured, ...placeholders]
  }, [
    constraints,
    constraintTypes,
    isFiltered,
    divisionFilter,
    teamFilter,
    divisionTargets,
    divisions,
    teams,
  ])

  // Auto-show tutorial on first visit
  useEffect(() => {
    if (
      !loading &&
      tableData.length > 0 &&
      !localStorage.getItem('sn_constraints_tutorial_seen')
    ) {
      setTutorialOpen(true)
    }
  }, [loading, tableData.length])

  const handleTutorialClose = useCallback(() => {
    setTutorialOpen(false)
    localStorage.setItem('sn_constraints_tutorial_seen', '1')
  }, [])

  // Real row IDs + the synthetic collapsed pair_matchup row for
  // SortableContext. The collapsed row IS draggable as a unit — when
  // moved, handleDragEnd expands it into its underlying constraint IDs
  // so every pair_matchup_count constraint gets a clustered sort_order
  // at the new position.
  const rowKeys = useMemo(
    () =>
      tableData
        .filter((r) => !r._isPlaceholder && !r._isCollapsedTarget)
        .map((r) => r.id),
    [tableData]
  )

  // The pair_matchup_count ids the VISIBLE collapsed legacy row represents.
  // Sourced from that row's _collapsedIds (built from the target-filtered pair
  // set), so bulk delete/toggle/type act on exactly what's shown and never
  // touch hidden, target-superseded pair rows.
  const collapsedPairIds = useMemo(() => {
    const row = tableData.find((r) => r._isCollapsed) as
      | CollapsedPairMatchupRow
      | undefined
    return row?._collapsedIds ?? []
  }, [tableData])

  // The targeted division uids the VISIBLE Games Per Team row represents —
  // the set that toggle-off / Delete reverts to fill.
  const collapsedTargetDivisionUids = useMemo(() => {
    const row = tableData.find((r) => r._isCollapsedTarget) as
      | CollapsedDivisionTargetRow
      | undefined
    return row?.division_uids ?? []
  }, [tableData])

  // DnD handlers
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id)
  }, [])

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event
      setActiveId(null)

      if (!over || active.id === over.id) {
        return
      }

      const oldIndex = rowKeys.indexOf(String(active.id))
      const newIndex = rowKeys.indexOf(String(over.id))

      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        const reordered = [...rowKeys]
        const [removed] = reordered.splice(oldIndex, 1)
        reordered.splice(newIndex, 0, removed)

        // Only include enabled rows in the reorder payload;
        // disabled rows keep their current sort_order.
        const enabledIds = new Set(
          tableData
            .filter((r) => !r._isPlaceholder && r.is_enabled)
            .map((r) => r.id)
        )

        // The collapsed pair_matchup synthetic id needs expansion into
        // all underlying constraint IDs so each one gets a clustered
        // sort_order at the new position.
        const collapsedRow = tableData.find((r) => r._isCollapsed) as
          | CollapsedPairMatchupRow
          | undefined

        const reorderPayload: ReorderItem[] = []
        let position = 0
        for (const id of reordered) {
          if (id === COLLAPSED_PAIR_MATCHUP_ID && collapsedRow) {
            // Insert all underlying constraint IDs here at consecutive
            // sort_orders so they cluster together at the new position.
            for (const collapsedId of collapsedRow._collapsedIds) {
              reorderPayload.push({ id: collapsedId, sort_order: position })
              position += 1
            }
            continue
          }
          if (enabledIds.has(id)) {
            reorderPayload.push({ id, sort_order: position })
            position += 1
          }
        }

        try {
          await reorderConstraints(reorderPayload)
        } catch {
          message.error('Failed to reorder constraints')
        }
      }
    },
    [rowKeys, tableData, reorderConstraints, message]
  )

  const handleAddModalClose = useCallback(() => {
    setIsAddModalOpen(false)
    setEditingConstraint(null)
    setPreSelectedTypeCode(null)
  }, [])

  const handleAddConstraintSubmit = useCallback(
    async (data: CreateConstraintData) => {
      await addConstraint(data)
    },
    [addConstraint]
  )

  // Build the team-grouped division shape MatchupMatrixModal expects from
  // the flat (uid, name) divisions + (uid, name, divisionUid) teams props.
  const matrixDivisions = useMemo<MatchupMatrixModalDivision[]>(
    () =>
      divisions.map((d) => ({
        id: d.uid,
        name: d.name,
        teams: teams
          .filter((t) => t.divisionUid === d.uid)
          .map((t) => ({ id: t.uid, name: t.name })),
      })),
    [divisions, teams]
  )

  const matrixDivisionsWithEnoughTeams = useMemo(
    () => matrixDivisions.filter((d) => d.teams.length >= 2),
    [matrixDivisions]
  )

  // Open the merged matrix modal. The modal owns division selection
  // in-place via its own dropdown; we only forward a preferred starting
  // division when it's explicitly known (e.g. opened from a row already
  // scoped to a specific division). With no preference the modal defaults
  // to its "All Divisions" view, which is the most common entry point.
  const requestOpenMatchupConfig = useCallback(
    (preferredDivisionId?: string | null) => {
      if (matrixDivisionsWithEnoughTeams.length === 0) {
        message.info(
          'Add at least one division with two or more teams before configuring games per team.'
        )
        return
      }
      const starting =
        preferredDivisionId &&
        matrixDivisionsWithEnoughTeams.some((d) => d.id === preferredDivisionId)
          ? preferredDivisionId
          : null
      setMatchupInitialDivisionId(starting)
      setMatchupModalOpen(true)
    },
    [matrixDivisionsWithEnoughTeams, message]
  )

  // Map a pair_matchup_count constraint back to the division it applies to.
  // Prefer division_uids when set; otherwise look up the team's division.
  const findDivisionIdForConstraint = useCallback(
    (constraint: SchedulerConstraint): string | null => {
      if (constraint.division_uids && constraint.division_uids.length > 0) {
        return constraint.division_uids[0]
      }
      const firstTeamUid = constraint.team_uids?.[0]
      if (!firstTeamUid) {
        return null
      }
      return teams.find((t) => t.uid === firstTeamUid)?.divisionUid ?? null
    },
    [teams]
  )

  const handleEditConstraint = useCallback(
    (constraintId: string) => {
      // The collapsed pair_matchup summary AND the canonical division-target
      // summary both route straight to the matrix modal (the single source of
      // truth for games-per-team). No specific division — open All Divisions.
      if (
        constraintId === COLLAPSED_PAIR_MATCHUP_ID ||
        constraintId === COLLAPSED_DIVISION_TARGET_ID
      ) {
        requestOpenMatchupConfig()
        return
      }
      const constraint = constraints.find((c) => c.id === constraintId)
      if (!constraint) {
        return
      }
      // Individual pair_matchup_count rows shouldn't reach this branch
      // anymore (they're collapsed at table-data level), but keep the
      // routing as a safety net.
      if (constraint.type.code === 'pair_matchup_count') {
        const divisionId = findDivisionIdForConstraint(constraint)
        requestOpenMatchupConfig(divisionId)
        return
      }
      setEditingConstraint(constraint)
      setIsAddModalOpen(true)
    },
    [constraints, findDivisionIdForConstraint, requestOpenMatchupConfig]
  )

  const handleUpdateConstraint = useCallback(
    async (
      constraintId: string,
      data: Partial<{
        parameters: Record<string, unknown>
        description: string
        is_hard: boolean
        is_enabled: boolean
        division_uids: string[] | null
        team_uids: string[] | null
      }>
    ) => {
      await updateConstraint(constraintId, data)
      setIsAddModalOpen(false)
      setEditingConstraint(null)
    },
    [updateConstraint]
  )

  const handleDeleteConstraint = useCallback(
    (constraintId: string) => {
      // The Games Per Team summary row "deletes" by clearing its target —
      // those divisions revert to filling the available slots.
      if (constraintId === COLLAPSED_DIVISION_TARGET_ID) {
        const uids = collapsedTargetDivisionUids
        if (uids.length === 0 || !clearDivisionTargets) {
          return
        }
        modal.confirm({
          title: 'Clear games-per-team target',
          content:
            uids.length === 1
              ? 'Remove the games-per-team target? This division will go back to filling the available slots.'
              : `Remove the games-per-team target across ${uids.length} divisions? They will go back to filling the available slots.`,
          okText: 'Clear target',
          okType: 'danger',
          cancelText: 'Cancel',
          onOk: async () => {
            try {
              await clearDivisionTargets(uids)
              await reloadConstraints?.()
            } catch {
              message.error('Failed to clear games-per-team target')
            }
          },
        })
        return
      }
      // Bulk-delete the pair_matchup_count rows the collapsed summary
      // represents (its visible _collapsedIds, not all pair rows).
      if (constraintId === COLLAPSED_PAIR_MATCHUP_ID) {
        const ids = collapsedPairIds
        if (ids.length === 0) {
          return
        }
        modal.confirm({
          title: 'Delete all games-per-team overrides',
          content: `Delete all ${ids.length} per-pair matchup row${
            ids.length === 1 ? '' : 's'
          }? Pairs will fall back to the solver default.`,
          okText: 'Delete all',
          okType: 'danger',
          cancelText: 'Cancel',
          onOk: async () => {
            try {
              await Promise.all(ids.map((id) => deleteConstraint(id)))
            } catch {
              message.error('Failed to delete some games-per-team rows')
            }
          },
        })
        return
      }
      modal.confirm({
        title: 'Delete Constraint',
        content: 'Are you sure you want to delete this constraint?',
        okText: 'Delete',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            await deleteConstraint(constraintId)
          } catch {
            message.error('Failed to delete constraint')
          }
        },
      })
    },
    [
      collapsedPairIds,
      collapsedTargetDivisionUids,
      clearDivisionTargets,
      reloadConstraints,
      deleteConstraint,
      message,
      modal,
    ]
  )

  const handleTypeChange = useCallback(
    async (constraintId: string, isHard: boolean) => {
      // The target row exposes no hard/soft control — guard defensively.
      if (constraintId === COLLAPSED_DIVISION_TARGET_ID) {
        return
      }
      // Bulk-update is_hard on the pair rows the collapsed summary represents.
      if (constraintId === COLLAPSED_PAIR_MATCHUP_ID) {
        try {
          await Promise.all(
            collapsedPairIds.map((id) =>
              updateConstraint(id, { is_hard: isHard })
            )
          )
        } catch {
          message.error('Failed to update some games-per-team rows')
        }
        return
      }
      try {
        await updateConstraint(constraintId, { is_hard: isHard })
      } catch {
        message.error('Failed to update constraint type')
      }
    },
    [collapsedPairIds, updateConstraint, message]
  )

  // Wrapper around toggleEnabled that fans out to every underlying
  // pair_matchup_count row when the user toggles the collapsed summary.
  const handleToggleEnabled = useCallback(
    async (constraintId: string, enabled: boolean) => {
      // The Games Per Team summary toggle clears the target when turned off
      // (those divisions revert to fill). The row is only shown while a target
      // exists, so it's always "on" — an "on" toggle has nothing to do.
      if (constraintId === COLLAPSED_DIVISION_TARGET_ID) {
        const uids = collapsedTargetDivisionUids
        if (enabled || uids.length === 0 || !clearDivisionTargets) {
          return
        }
        try {
          await clearDivisionTargets(uids)
          await reloadConstraints?.()
        } catch {
          message.error('Failed to clear games-per-team target')
        }
        return
      }
      if (constraintId === COLLAPSED_PAIR_MATCHUP_ID) {
        try {
          await Promise.all(
            collapsedPairIds.map((id) => toggleEnabled(id, enabled))
          )
        } catch {
          message.error('Failed to toggle some games-per-team rows')
        }
        return
      }
      await toggleEnabled(constraintId, enabled)
    },
    [
      collapsedPairIds,
      collapsedTargetDivisionUids,
      clearDivisionTargets,
      reloadConstraints,
      toggleEnabled,
      message,
    ]
  )

  const handleCloneConstraint = useCallback(
    (constraintId: string) => {
      // Clone of either summary row makes no sense as a single row — both route
      // to the modal, the single source of truth for games-per-team.
      if (
        constraintId === COLLAPSED_DIVISION_TARGET_ID ||
        constraintId === COLLAPSED_PAIR_MATCHUP_ID
      ) {
        requestOpenMatchupConfig()
        return
      }
      const constraint = constraints.find((c) => c.id === constraintId)
      if (!constraint) {
        return
      }
      // Cloning a pair_matchup_count row in isolation makes no sense — the
      // matrix is the source of truth. Open the grid to the same division.
      if (constraint.type.code === 'pair_matchup_count') {
        const divisionId = findDivisionIdForConstraint(constraint)
        requestOpenMatchupConfig(divisionId)
        return
      }
      setEditingConstraint({ ...constraint, id: '__clone__' })
      setIsAddModalOpen(true)
    },
    [constraints, findDivisionIdForConstraint, requestOpenMatchupConfig]
  )

  const handleConfigurePlaceholder = useCallback(
    (typeCode: string) => {
      // The matrix modal is the only entry point for pair_matchup_count —
      // route placeholder clicks there instead of the generic add form.
      if (typeCode === 'pair_matchup_count') {
        requestOpenMatchupConfig()
        return
      }
      setPreSelectedTypeCode(typeCode)
      setEditingConstraint(null)
      setIsAddModalOpen(true)
    },
    [requestOpenMatchupConfig]
  )

  // --- Render helpers ---

  // Is ANY pair-specific override live? A PAIR_MATCHUP_COUNT constraint is HARD
  // and WINS over the games-per-team target, and the division total takes no
  // account of what it consumes — so with one enabled we cannot promise "at least
  // N per team" (measured: 5 teams x 25 with an A-B override of 20 puts three
  // teams on 19). We branch on PRESENCE only; predicting the resulting per-team
  // numbers would mean reproducing the backend's balancing algorithm here.
  const hasEnabledPairOverride = constraints.some(
    (c) => c.type.code === 'pair_matchup_count' && c.is_enabled
  )

  const renderTypeBadge = useCallback(
    (constraintId: string, isHard: boolean, readOnly = false) => (
      <div
        css={css`
          display: flex;
          border-radius: 4px;
          overflow: hidden;
        `}
      >
        <Tooltip
          title={
            readOnly
              ? gamesPerTeamPromiseCopy(hasEnabledPairOverride)
              : 'Required — must be met to generate schedule'
          }
          placement='top'
        >
          <button
            type='button'
            onClick={
              readOnly
                ? undefined
                : (e) => {
                    e.stopPropagation()
                    handleTypeChange(constraintId, true)
                  }
            }
            css={css`
              font-family: 'Inter', sans-serif;
              font-size: 10px;
              font-weight: 600;
              line-height: 12px;
              letter-spacing: 1.6px;
              padding: 4px 8px;
              background-color: ${isHard
                ? colors.ERROR_500
                : colors.SECONDARY_400};
              color: ${colors.NEUTRAL_30};
              opacity: ${isHard ? 1 : 0.5};
              text-transform: uppercase;
              border: none;
              cursor: ${readOnly ? 'default' : 'pointer'};
              transition: all 0.2s ease;

              ${!readOnly &&
              css`
                &:hover {
                  opacity: 1;
                  background-color: ${isHard
                    ? colors.ERROR_500
                    : colors.ERROR_400};
                }
              `}
            `}
          >
            HARD
          </button>
        </Tooltip>
        <Tooltip
          title={
            readOnly
              ? gamesPerTeamPromiseCopy(hasEnabledPairOverride)
              : 'Preference — scheduler tries to satisfy'
          }
          placement='top'
        >
          <button
            type='button'
            onClick={
              readOnly
                ? undefined
                : (e) => {
                    e.stopPropagation()
                    handleTypeChange(constraintId, false)
                  }
            }
            css={css`
              font-family: 'Inter', sans-serif;
              font-size: 10px;
              font-weight: 600;
              line-height: 12px;
              letter-spacing: 1.6px;
              padding: 4px 8px;
              background-color: ${!isHard
                ? colors.INFO_500
                : colors.SECONDARY_400};
              color: ${colors.NEUTRAL_30};
              opacity: ${!isHard ? 1 : 0.5};
              text-transform: uppercase;
              border: none;
              cursor: ${readOnly ? 'default' : 'pointer'};
              transition: all 0.2s ease;

              ${!readOnly &&
              css`
                &:hover {
                  opacity: 1;
                  background-color: ${!isHard
                    ? colors.INFO_500
                    : colors.INFO_400};
                }
              `}
            `}
          >
            SOFT
          </button>
        </Tooltip>
      </div>
    ),
    [handleTypeChange, hasEnabledPairOverride]
  )

  // Table columns
  const columns: ColumnsType<TableRow> = useMemo(
    () => [
      {
        title: 'Priority',
        key: 'rank',
        // Renders uppercase via darkTableStyles. 'Priority' is the short form of
        // James's 'Prioritization' — the full word needs ~133px at 12px caps
        // plus 32px cell padding, which is disproportionate for a column holding
        // a single number and eats width from DESCRIPTION.
        width: 96,
        onCell: () => ({ className: 'rank-cell' }),
        render: (_: unknown, record: TableRow, index: number) => {
          if (record._isPlaceholder || !record.is_enabled) {
            return (
              <span
                css={css`
                  color: ${colors.NEUTRAL_100};
                `}
              >
                —
              </span>
            )
          }
          // Collapsed rows fall through to the same grip + number render
          // as enabled constraints — visually consistent. Their drag
          // behavior is handled in handleDragEnd by expanding the
          // synthetic id into the underlying constraint ids.
          return (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                cursor: ${isFiltered ? 'default' : 'grab'};
                &:active {
                  cursor: ${isFiltered ? 'default' : 'grabbing'};
                }
              `}
            >
              {!isFiltered && (
                <Icon
                  name='grip-lines'
                  fontSize={14}
                  color={colors.NEUTRAL_100}
                />
              )}
              <span>{index + 1}</span>
            </div>
          )
        },
      },
      {
        title: 'Description',
        key: 'description',
        onCell: () => ({ className: 'description-cell' }),
        render: (_: unknown, record: TableRow) => {
          if (record._isCollapsedTarget) {
            const typeDescription =
              constraintTypes.find((t) => t.code === record.type.code)
                ?.description || null
            return (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 2px;
                  overflow: hidden;
                `}
              >
                <span
                  css={[
                    constraintTitleStyles,
                    css`
                      color: ${colors.WHITE};
                      overflow: hidden;
                      text-overflow: ellipsis;
                      white-space: nowrap;
                    `,
                  ]}
                >
                  {getConstraintDisplayName(record.type.code, record.type.name)}
                </span>
                {typeDescription && (
                  <span css={constraintDescriptionStyles}>
                    {typeDescription}
                  </span>
                )}
                <span
                  css={css`
                    display: inline-block;
                    align-self: flex-start;
                    font-size: 12px;
                    font-weight: 500;
                    line-height: 1.3;
                    padding: 1px 6px;
                    border-radius: 4px;
                    color: ${colors.PRIMARY};
                    background-color: ${colors.PRIMARY}14;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 100%;
                  `}
                >
                  {record._targetSummary}
                </span>
              </div>
            )
          }
          if (record._isCollapsed) {
            const { pairCount, countSummary, scopeSummary } = record._summary
            // Sum per-team game counts directly from the underlying
            // pair_matchup_count constraints. We deliberately count ONLY
            // pairs that have explicit constraints (no implicit defaults),
            // which keeps the chip honest across multi-division setups
            // where cross-division pairs are intentionally absent.
            const collapsedConstraints = constraints.filter(
              (c) =>
                c.type.code === PAIR_MATCHUP_COUNT_CODE &&
                record._collapsedIds.includes(c.id)
            )
            const perTeamSum: Record<string, number> = {}
            for (const uid of record.team_uids ?? []) {
              perTeamSum[uid] = 0
            }
            // Sum of all per-pair counts = total league games (each
            // constraint represents one bilateral pair, count = games
            // played between them).
            let totalLeagueGames = 0
            for (const c of collapsedConstraints) {
              const teamUidsArr = c.team_uids ?? []
              if (teamUidsArr.length !== 2) {
                continue
              }
              const cnt = c.parameters.count
              if (typeof cnt !== 'number') {
                continue
              }
              perTeamSum[teamUidsArr[0]] =
                (perTeamSum[teamUidsArr[0]] ?? 0) + cnt
              perTeamSum[teamUidsArr[1]] =
                (perTeamSum[teamUidsArr[1]] ?? 0) + cnt
              totalLeagueGames += cnt
            }
            const perTeamValues = Object.values(perTeamSum)
            const allSamePerTeam =
              perTeamValues.length > 0 &&
              perTeamValues.every((v) => v === perTeamValues[0])
            const perTeamLabel = allSamePerTeam
              ? `${perTeamValues[0]} per team`
              : 'varies per team'
            const chipText = `${countSummary} · ${perTeamLabel} · ${totalLeagueGames} total games`
            const typeDescription =
              constraintTypes.find((t) => t.code === record.type.code)
                ?.description || null
            return (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 2px;
                  overflow: hidden;
                `}
              >
                <span
                  css={[
                    constraintTitleStyles,
                    css`
                      color: ${colors.WHITE};
                      overflow: hidden;
                      text-overflow: ellipsis;
                      white-space: nowrap;
                    `,
                  ]}
                >
                  {getConstraintDisplayName(record.type.code, record.type.name)}
                </span>
                {typeDescription && (
                  <span css={constraintDescriptionStyles}>
                    {typeDescription}
                  </span>
                )}
                {/* The only place the collapsed row states its pair count —
                    content, not chrome. Kept a step below the 14px description
                    so the row doesn't become three equal-weight lines. */}
                <span
                  css={css`
                    color: ${colors.NEUTRAL_50};
                    font-size: 12px;
                    line-height: 1.3;
                  `}
                >
                  {`${pairCount} pair${
                    pairCount === 1 ? '' : 's'
                  } configured · ${scopeSummary}`}
                </span>
                <span
                  css={css`
                    display: inline-block;
                    align-self: flex-start;
                    font-size: 12px;
                    font-weight: 500;
                    line-height: 1.3;
                    padding: 1px 6px;
                    border-radius: 4px;
                    color: ${colors.PRIMARY};
                    background-color: ${colors.PRIMARY}14;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 100%;
                  `}
                >
                  {chipText}
                </span>
              </div>
            )
          }
          if (record._isPlaceholder || !record.is_enabled) {
            const subDescription = record._isPlaceholder
              ? record.description
              : constraintTypes.find((t) => t.code === record.type.code)
                  ?.description || null
            const paramSummary = !record._isPlaceholder
              ? formatConstraintParameterSummary(
                  record.type.code,
                  record.parameters as Record<string, unknown>,
                  constraintEntityNames
                )
              : null
            return (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 2px;
                `}
              >
                <span
                  css={[
                    constraintTitleStyles,
                    css`
                      color: ${colors.NEUTRAL_50};
                    `,
                  ]}
                >
                  {getConstraintDisplayName(record.type.code, record.type.name)}
                </span>
                {subDescription && (
                  <span css={constraintDescriptionStyles}>
                    {subDescription}
                  </span>
                )}
                {paramSummary && (
                  <span
                    css={css`
                      display: inline-block;
                      align-self: flex-start;
                      font-size: 12px;
                      font-weight: 500;
                      line-height: 1.3;
                      padding: 1px 6px;
                      border-radius: 4px;
                      /* Muted background encodes "off"; the illegible
                         foreground never did that work, and the italic was
                         dropped as hard to read. */
                      color: ${colors.NEUTRAL_70};
                      background-color: rgba(51, 54, 59, 0.25);
                      white-space: nowrap;
                      overflow: hidden;
                      text-overflow: ellipsis;
                      max-width: 100%;
                    `}
                  >
                    {paramSummary}
                  </span>
                )}
              </div>
            )
          }
          const displayText =
            record.description ||
            getConstraintDisplayName(record.type.code, record.type.name)
          const typeDescription =
            constraintTypes.find((t) => t.code === record.type.code)
              ?.description || null
          const paramSummary = formatConstraintParameterSummary(
            record.type.code,
            record.parameters as Record<string, unknown>,
            constraintEntityNames
          )
          return (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 2px;
                overflow: hidden;
              `}
            >
              <span
                css={[
                  constraintTitleStyles,
                  css`
                    color: ${colors.WHITE};
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                  `,
                ]}
              >
                {displayText}
              </span>
              {typeDescription && (
                <span css={constraintDescriptionStyles}>{typeDescription}</span>
              )}
              {paramSummary && (
                <span
                  css={css`
                    display: inline-block;
                    align-self: flex-start;
                    font-size: 12px;
                    font-weight: 500;
                    line-height: 1.3;
                    padding: 1px 6px;
                    border-radius: 4px;
                    color: ${colors.PRIMARY};
                    background-color: ${colors.PRIMARY}14;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 100%;
                  `}
                >
                  {paramSummary}
                </span>
              )}
            </div>
          )
        },
      },
      {
        title: 'Divisions',
        key: 'divisions',
        width: 160,
        ellipsis: true,
        render: (_: unknown, record: TableRow) => {
          if (record._isCollapsedTarget) {
            return (
              <span
                css={css`
                  color: ${colors.NEUTRAL_30};
                  font-weight: 500;
                `}
              >
                {formatDivisions(record.division_uids, divisions)}
              </span>
            )
          }
          if (record._isCollapsed) {
            return (
              <span
                css={css`
                  color: ${colors.NEUTRAL_30};
                  font-weight: 500;
                `}
              >
                {record._summary.scopeSummary === 'league-wide'
                  ? 'All Divisions'
                  : record._summary.scopeSummary === 'mixed scope'
                  ? 'Mixed'
                  : formatDivisions(record.division_uids, divisions)}
              </span>
            )
          }
          const isMuted = record._isPlaceholder || !record.is_enabled
          return (
            <span
              css={css`
                color: ${isMuted ? colors.NEUTRAL_100 : colors.NEUTRAL_30};
                font-weight: 500;
              `}
            >
              {formatDivisions(record.division_uids, divisions)}
            </span>
          )
        },
      },
      {
        title: 'Teams',
        key: 'teams',
        width: 160,
        ellipsis: true,
        render: (_: unknown, record: TableRow) => {
          if (record._isCollapsedTarget) {
            // Division-level scope (team_uids is null) — the target applies to
            // every team in its targeted division(s).
            return (
              <span
                css={css`
                  color: ${colors.NEUTRAL_30};
                  font-weight: 500;
                `}
              >
                All Teams
              </span>
            )
          }
          if (record._isCollapsed) {
            const teamCount = (record.team_uids ?? []).length
            return (
              <span
                css={css`
                  color: ${colors.NEUTRAL_30};
                  font-weight: 500;
                `}
              >
                {`${teamCount} team${teamCount === 1 ? '' : 's'}`}
              </span>
            )
          }
          const isMuted = record._isPlaceholder || !record.is_enabled
          return (
            <span
              css={css`
                color: ${isMuted ? colors.NEUTRAL_100 : colors.NEUTRAL_30};
                font-weight: 500;
              `}
            >
              {formatTeams(record.team_uids, teams)}
            </span>
          )
        },
      },
      {
        title: 'Type',
        key: 'type',
        width: 130,
        align: 'center' as const,
        render: (_: unknown, record: TableRow) => {
          // Venue capacity is a physical limit — always hard. Render a static
          // HARD badge (no HARD|SOFT toggle) so the no-op SOFT choice isn't
          // offered; the backend enforces venue_capacity hard regardless.
          if (record.type.code === 'venue_capacity') {
            return (
              <span data-tour-type-badge>
                <Tooltip
                  title='Venue capacity is a physical limit — always enforced as a hard constraint'
                  placement='top'
                >
                  <span
                    css={css`
                      font-family: 'Inter', sans-serif;
                      font-size: 12px;
                      font-weight: 600;
                      line-height: 12px;
                      letter-spacing: 1.6px;
                      padding: 4px 8px;
                      border-radius: 4px;
                      text-transform: uppercase;
                      background-color: ${colors.ERROR_500};
                      color: ${colors.NEUTRAL_30};
                    `}
                  >
                    HARD
                  </span>
                </Tooltip>
              </span>
            )
          }
          // A games-per-team target is a HARD requirement the solver always
          // enforces, so render the same HARD|SOFT control as other rows in
          // read-only mode (HARD active, SOFT greyed) — display-only, since
          // it's not switchable.
          if (record._isCollapsedTarget) {
            return (
              <span data-tour-type-badge>
                {renderTypeBadge(record.id, record.is_hard, true)}
              </span>
            )
          }
          if (record._isPlaceholder || !record.is_enabled) {
            return (
              <span data-tour-type-badge>
                <span
                  css={css`
                    font-family: 'Inter', sans-serif;
                    font-size: 12px;
                    font-weight: 600;
                    line-height: 12px;
                    letter-spacing: 1.6px;
                    padding: 4px 8px;
                    border-radius: 4px;
                    text-transform: uppercase;
                    background-color: ${record.is_hard
                      ? colors.ERROR_500
                      : colors.INFO_500};
                    color: ${colors.NEUTRAL_30};
                    opacity: 0.5;
                  `}
                >
                  {record.is_hard ? 'HARD' : 'SOFT'}
                </span>
              </span>
            )
          }
          return (
            <span data-tour-type-badge>
              {renderTypeBadge(record.id, record.is_hard)}
            </span>
          )
        },
      },
      {
        title: 'Category',
        key: 'category',
        width: 136,
        render: (_: unknown, record: TableRow) =>
          renderCategoryBadge(mapCategoryToDisplay(record.type.category)),
      },
      {
        title: '',
        key: 'actions',
        width: 160,
        render: (_: unknown, record: TableRow) => {
          // The target row uses the SAME controls as other rows (toggle / edit
          // / clone / ⋯). Edit + clone reopen the modal; toggle-off + Delete
          // clear the target. It falls through to the shared block below.
          if (record._isPlaceholder) {
            return (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                `}
              >
                <Tooltip title='Enable — opens configuration' placement='top'>
                  <span data-tour-switch>
                    <Switch
                      checked={false}
                      onChange={() =>
                        handleConfigurePlaceholder(record.type.code)
                      }
                      onClick={(_checked, e) => e.stopPropagation()}
                    />
                  </span>
                </Tooltip>
                <span data-tour-edit>
                  <Tooltip title='Configure this constraint' placement='top'>
                    <button
                      type='button'
                      onClick={(e) => {
                        e.stopPropagation()
                        handleConfigurePlaceholder(record.type.code)
                      }}
                      css={css`
                        background: transparent;
                        border: none;
                        cursor: pointer;
                        padding: 4px;
                        display: flex;
                        align-items: center;
                        color: ${colors.PRIMARY};
                        &:hover {
                          color: ${colors.PRIMARY_HOVER};
                        }
                      `}
                    >
                      <Icon name='pen' fontSize={16} color='currentColor' />
                    </button>
                  </Tooltip>
                </span>
              </div>
            )
          }
          return (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
              `}
            >
              <span data-tour-switch>
                <Switch
                  checked={record.is_enabled}
                  onChange={(checked) =>
                    handleToggleEnabled(record.id, checked)
                  }
                  css={css`
                    &.ant-switch-checked {
                      background-color: ${colors.PRIMARY} !important;
                    }
                  `}
                  onClick={(_checked, e) => e.stopPropagation()}
                />
              </span>
              <span data-tour-edit>
                <Tooltip title='Edit' placement='top'>
                  <button
                    type='button'
                    onClick={(e) => {
                      e.stopPropagation()
                      handleEditConstraint(record.id)
                    }}
                    css={css`
                      background: transparent;
                      border: none;
                      cursor: pointer;
                      padding: 4px;
                      display: flex;
                      align-items: center;
                      color: ${colors.PRIMARY};
                      &:hover {
                        color: ${colors.PRIMARY_HOVER};
                      }
                    `}
                  >
                    <Icon name='pen' fontSize={16} color='currentColor' />
                  </button>
                </Tooltip>
              </span>
              <Tooltip title='Clone' placement='top'>
                <button
                  type='button'
                  onClick={(e) => {
                    e.stopPropagation()
                    handleCloneConstraint(record.id)
                  }}
                  css={css`
                    background: transparent;
                    border: none;
                    cursor: pointer;
                    padding: 4px;
                    display: flex;
                    align-items: center;
                    color: ${colors.PRIMARY};
                    &:hover {
                      color: ${colors.PRIMARY_HOVER};
                    }
                  `}
                >
                  <Icon name='copy' fontSize={16} color='currentColor' />
                </button>
              </Tooltip>
              <Dropdown
                menu={{
                  items: [
                    ...(record.is_enabled
                      ? [
                          {
                            key: 'delete',
                            label: 'Delete',
                            danger: true,
                            onClick: () => handleDeleteConstraint(record.id),
                          } as NonNullable<MenuProps['items']>[number],
                        ]
                      : []),
                  ],
                }}
                trigger={['click']}
                placement='bottomRight'
              >
                <Tooltip title='More Options' placement='top'>
                  <button
                    type='button'
                    onClick={(e) => e.stopPropagation()}
                    css={css`
                      background: transparent;
                      border: none;
                      cursor: pointer;
                      padding: 4px;
                      display: flex;
                      align-items: center;
                      color: ${colors.NEUTRAL_100};
                      &:hover {
                        color: ${colors.WHITE};
                      }
                    `}
                  >
                    <Icon
                      name='ellipsis-v'
                      fontSize={16}
                      color='currentColor'
                    />
                  </button>
                </Tooltip>
              </Dropdown>
            </div>
          )
        },
      },
    ],
    [
      divisions,
      teams,
      constraintTypes,
      constraints,
      constraintEntityNames,
      handleToggleEnabled,
      handleEditConstraint,
      handleCloneConstraint,
      handleDeleteConstraint,
      handleConfigurePlaceholder,
      renderTypeBadge,
      isFiltered,
    ]
  )

  // Active constraint for drag overlay (only real rows are draggable)
  const activeConstraint = activeId
    ? tableData.find((c) => !c._isPlaceholder && c.id === String(activeId))
    : null

  const isLoading = loading && tableData.length === 0

  return (
    <>
      {/* Header */}
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 16px;
        `}
      >
        <div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              margin-bottom: 4px;
            `}
          >
            <h2
              css={css`
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                font-weight: 600;
                line-height: 16.8px;
                letter-spacing: 1.68px;
                text-transform: uppercase;
                color: ${colors.NEUTRAL_0};
                margin: 0;
              `}
            >
              Constraints
            </h2>
            <Tooltip title='Constraint Help' placement='bottom'>
              <button
                type='button'
                onClick={() => setTutorialOpen(true)}
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  width: 18px;
                  height: 18px;
                  border-radius: 50%;
                  border: 1px solid ${colors.PRIMARY};
                  background: transparent;
                  cursor: pointer;
                  padding: 0;
                  font-family: 'Inter', sans-serif;
                  font-size: 11px;
                  font-weight: 600;
                  color: ${colors.PRIMARY};
                  line-height: 1;
                  transition: all 0.2s ease;

                  &:hover {
                    border-color: ${colors.PRIMARY_HOVER};
                    color: ${colors.PRIMARY_HOVER};
                  }
                `}
              >
                ?
              </button>
            </Tooltip>
          </div>
          <p
            css={css`
              font-family: 'Inter', sans-serif;
              font-size: 12px;
              font-weight: 400;
              line-height: 17.4px;
              color: ${colors.NEUTRAL_50};
              margin: 0;
            `}
          >
            Add and configure scheduling constraints. Hard constraints must be
            met to generate a schedule. Soft constraints are preferences the
            scheduler tries to satisfy.
          </p>
        </div>
        <div
          css={css`
            display: flex;
            gap: 8px;
            align-items: center;
          `}
        >
          <button
            type='button'
            onClick={() => setIsAiSuggestModalOpen(true)}
            css={outlineAccentButtonStyles}
          >
            <Icon name='magic' fontSize={14} color='currentColor' />
            AI Suggest
          </button>
          <button
            type='button'
            onClick={() => {
              setEditingConstraint(null)
              setPreSelectedTypeCode(null)
              setIsAddModalOpen(true)
            }}
            css={outlinePrimaryButtonStyles}
          >
            <Icon name='plus' fontSize={14} color='currentColor' />
            Add Constraint
          </button>
        </div>
      </div>

      {/* Filter bar */}
      {divisions.length > 0 && teams.length > 0 && (
        <div
          css={css`
            display: flex;
            gap: 16px;
            margin-bottom: 16px;
          `}
        >
          <div
            css={css`
              flex: 1;
            `}
          >
            <FilterDropdown
              label='Division'
              value={divisionFilter}
              allLabel='All Divisions'
              options={divisionFilterOptions}
              onChange={handleDivisionFilterChange}
            />
          </div>
          <div
            css={css`
              flex: 1;
            `}
          >
            <FilterDropdown
              label='Team'
              value={teamFilter}
              allLabel='All Teams'
              options={teamFilterOptions}
              onChange={setTeamFilter}
            />
          </div>
        </div>
      )}

      {isLoading ? (
        <div
          css={css`
            display: flex;
            justify-content: center;
            padding: 48px;
          `}
        >
          <Spin size='large' />
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          modifiers={[restrictToVerticalAxis]}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div>
            <SortableContext
              items={rowKeys}
              strategy={verticalListSortingStrategy}
            >
              <div
                css={[
                  darkTableStyles,
                  css`
                    .placeholder-row td {
                      opacity: 0.55;
                      transition: opacity 0.2s ease;
                    }
                    .placeholder-row:hover td {
                      opacity: 0.85;
                    }
                    /* opacity composites the whole <td> subtree, so no child
                       colour can undo it — the Description cell has to opt out
                       at the cell level. Both classes are listed because
                       rowClassName adds disabled-row AND placeholder-row to a
                       placeholder. Disabled rows stay legible AS disabled via
                       the em-dash priority, the NEUTRAL_50 title, the off
                       switch, and every other column still at 0.5. */
                    .ant-table-tbody > tr.disabled-row > td.description-cell,
                    .ant-table-tbody
                      > tr.placeholder-row
                      > td.description-cell {
                      opacity: 1;
                    }
                  `,
                ]}
              >
                <Table
                  dataSource={tableData}
                  columns={columns}
                  rowKey='id'
                  pagination={false}
                  size='small'
                  locale={{
                    emptyText: (
                      <div
                        css={css`
                          padding: 32px;
                          color: ${colors.NEUTRAL_50};
                          font-family: 'Inter', sans-serif;
                        `}
                      >
                        {isFiltered
                          ? 'No constraints match the selected filters.'
                          : 'No constraints added yet. Click "+ Add" to create your first constraint.'}
                      </div>
                    ),
                  }}
                  rowClassName={(record) => {
                    const classes: string[] = []
                    if (!record.is_enabled) {
                      classes.push('disabled-row')
                    }
                    if ((record as TableRow)._isPlaceholder) {
                      classes.push('placeholder-row')
                    }
                    return classes.join(' ')
                  }}
                  components={{
                    body: {
                      row: DraggableRow,
                    },
                  }}
                />
              </div>
            </SortableContext>

            <DragOverlay>
              {activeConstraint ? (
                <div
                  css={css`
                    background-color: ${colors.SECONDARY_300};
                    padding: 12px 16px;
                    border-radius: 8px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
                    color: ${colors.WHITE};
                    font-family: 'Inter', sans-serif;
                    font-size: 14px;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                  `}
                >
                  <Icon
                    name='grip-lines'
                    fontSize={14}
                    color={colors.NEUTRAL_100}
                  />
                  <span>
                    {getConstraintDisplayName(
                      activeConstraint.type.code,
                      activeConstraint.type.name
                    )}
                  </span>
                  {renderCategoryBadge(
                    mapCategoryToDisplay(activeConstraint.type.category)
                  )}
                  <span
                    css={css`
                      padding: 2px 6px;
                      border-radius: 4px;
                      font-size: 10px;
                      font-weight: 600;
                      color: ${colors.NEUTRAL_30};
                      background-color: ${activeConstraint.is_hard
                        ? colors.ERROR_500
                        : colors.INFO_500};
                    `}
                  >
                    {activeConstraint.is_hard ? 'HARD' : 'SOFT'}
                  </span>
                </div>
              ) : null}
            </DragOverlay>
          </div>
        </DndContext>
      )}

      <AddConstraintModal
        open={isAddModalOpen}
        onClose={handleAddModalClose}
        onSubmit={handleAddConstraintSubmit}
        onUpdate={handleUpdateConstraint}
        divisions={divisions}
        teams={teams}
        venues={venuesForModal}
        facilities={facilitiesForModal}
        existingConstraints={constraints}
        editingConstraint={editingConstraint}
        preSelectedTypeCode={preSelectedTypeCode}
      />

      <AiSuggestModal
        open={isAiSuggestModalOpen}
        onClose={() => setIsAiSuggestModalOpen(false)}
        flightId={_flightId}
        onAccepted={
          reloadConstraints
            ? async () => {
                await reloadConstraints()
                // Defensive: AI Suggest writes pair_matchup_count today, not
                // targets, but refresh both so the list can't drift if it does.
                await reloadDivisionTargets?.()
              }
            : undefined
        }
      />

      <MatchupMatrixModal
        open={matchupModalOpen}
        divisions={matrixDivisions}
        initialDivisionId={matchupInitialDivisionId}
        defaultGamesPerMatchup={defaultGamesPerMatchup}
        existingConstraints={constraints.filter(
          (c) => c.type.code === 'pair_matchup_count'
        )}
        timeSlots={timeSlots}
        flightStartDate={flightStartDate}
        flightEndDate={flightEndDate}
        flightId={_flightId}
        onCancel={() => setMatchupModalOpen(false)}
        onSaved={async () => {
          // The modal persists the target to /division-targets AND deletes the
          // legacy pair rows — refresh both sources so the new "Games Per Team"
          // row appears immediately. Always close, even if a refetch rejects.
          try {
            await Promise.allSettled([
              reloadConstraints?.(),
              reloadDivisionTargets?.(),
            ])
          } finally {
            setMatchupModalOpen(false)
          }
        }}
        bulkSaveConstraints={bulkSaveConstraints}
      />

      <ConstraintsTutorial open={tutorialOpen} onClose={handleTutorialClose} />
    </>
  )
}

export default ConstraintsSection
